import React from "react";
import { AppProps } from "next/app";
import "tailwindcss/tailwind.css";
import "@styles/global.scss";
import "rc-tooltip/assets/bootstrap_white.css";
import { Provider } from "react-redux";
import store from "@redux/store";
import DomainWrapper from "@components/layouts/domainWrapper";
import { DomainProvider } from "@hooks/useDomainContext";
import { FormProvider } from "@hooks/useFormContext";
import { PageProps } from "@services/initial-calls";
import NextErrorComponent from "next/error";
import OutBrain from "@components/shared/outBrain";
import { CallCampaignProvider } from "@hooks/useCallCampaignContext";

import { datadogRum } from "@datadog/browser-rum";

datadogRum.init({
    applicationId: process.env.NEXT_PUBLIC_DATADOG_APP_ID ?? "",
    clientToken: process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN ?? "",
    site: "datadoghq.com",
    service: "landers",
    env: process.env.NEXT_PUBLIC_DATADOG_ENV ?? "",
    // Specify a version number to identify the deployed version of your application in Datadog
    // version: '1.0.0',
    sessionSampleRate: 100,
    sessionReplaySampleRate: 20,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: "mask-user-input",
    // Specify URLs to propagate trace headers for connection between RUM and backend trace
    allowedTracingUrls: [(url) => url.includes(".com/api/")],
});

export interface CustomAppProps<P = PageProps> extends AppProps<P> {
    pageProps: PageProps;
}

function MyApp({ Component, pageProps }: CustomAppProps): JSX.Element {
    if (pageProps.error) {
        return <NextErrorComponent statusCode={pageProps.error.statusCode} />;
    }

    return (
        <DomainProvider {...pageProps}>
            <FormProvider>
                <div id="domain-wrapper">
                    <DomainWrapper
                        domain={pageProps.domain}
                        pageTitle={pageProps.pageTitle}
                    >
                        <CallCampaignProvider>
                            <Provider store={store}>
                                <OutBrain />
                                <Component {...pageProps} />
                            </Provider>
                        </CallCampaignProvider>
                    </DomainWrapper>
                </div>
            </FormProvider>
        </DomainProvider>
    );
}

export default MyApp;
